import React, { FC, ReactNode, Suspense, useEffect, useState } from 'react';

import { getWritingDir } from '@rbilabs/intl';
import { isNil } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { Meta } from 'react-meta-elements';
import { Route, Routes, useMatch } from 'react-router-dom';
import styled from 'styled-components';
import { isFalse } from 'utils';

import AppHeader from 'components/app-header';
import { AutoSelectRestaurant } from 'components/auto-select-restaurant';
import { BottomServiceMode } from 'components/bottom-service-mode';
import { BottomSheetRefillDrinks } from 'components/bottom-sheet-refill-drinks';
import ErrorNetwork from 'components/error-network';
import { useFeatureHomePage } from 'components/features/home-page/use-feature-home-page';
import Footer from 'components/footer';
import { ForceUpdateModal } from 'components/force-update-modal';
import LayoutBottomContainer from 'components/layout/layout-bottom-container';
import LoadingAnimation from 'components/loading-animation';
import MobileNavigation from 'components/mobile-web-navigation-bar';
import ModalBeeper from 'components/modal-beeper';
import ModalLockerPickup from 'components/modal-lockers-pickup';
import { ScrollView } from 'components/scroll-view';
import SetLoading from 'components/set-loading';
import { ToastContainer } from 'components/toast';
import { TopServiceModeNotification } from 'components/top-service-mode-notification';
import { View } from 'components/view';
import {
  useFeatureAccountFormQuery,
  useFeatureAccountQuery,
  useFeatureMenuQuery,
} from 'generated/sanity-graphql';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useResetStoreResetCartResetServiceModeTimeout } from 'hooks/reset-store-timeout';
import useEffectOnce from 'hooks/use-effect-once';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { useFeatureMemberships } from 'hooks/use-feature-memberships';
import { useFeatureNavigation } from 'hooks/use-feature-navigation';
import { useForter } from 'hooks/use-forter';
import { useGeoData } from 'hooks/use-geo-data';
import { useIosAtt } from 'hooks/use-ios-att';
import { useTrendingData } from 'hooks/use-trending-data';
import { AboutApp } from 'pages/about-app';
import LanguageSelectorPage from 'pages/language-selector';
import LoyaltyNotAvailableAtDeliveryMode from 'pages/loyalty/loyalty-not-available-at-delivery-mode';
import LoyaltyNotAvailableAtThisRestaurant from 'pages/loyalty/loyalty-not-available-at-this-restaurant';
import LoyaltyOptInModal from 'pages/loyalty/loyalty-opt-in-modal';
import { useBluecodeContext } from 'state/bluecode';
import { useFeaturesContext } from 'state/features';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { NEXT_URL_QUERY_PARAM } from 'state/location/constants';
import { useLoyaltyContext } from 'state/loyalty';
import { useIsLoyaltyEnabled } from 'state/loyalty/hooks/use-is-loyalty-enabled';
import { useOrderContext } from 'state/order';
import { useScrollContext } from 'state/scroll';
import { usePageManagerContext } from 'state/static-page-manager';
import { useStaticPageRoutes } from 'state/static-page-manager/hooks/use-static-page-routes';
import { getIosAppTrackingStatus } from 'utils/get-ios-app-tracking-status';
import { Status } from 'utils/get-ios-app-tracking-status/types';
import { askPermissionOnCritialRoute, getShouldReloadApp } from 'utils/live-updates';
import { enablePush } from 'utils/permissions';
import { routes } from 'utils/routing';
import { shouldUseRobotsMetaTag } from 'utils/routing/bots';

import { brandRoutes, fullPageBrandRoutes } from './brand-routes';
import { CookieBanner } from './cookie-banner-container';
import { FabContainer } from './fab-container/fab-container';
import useLogPageView from './use-log-page-view';
import useScrollTop from './use-scroll-top';
import { LazyRoute, lazyWithFallback } from './util';

const Cart: LazyRoute = lazyWithFallback(() => import('pages/cart'));
const MainMenu: LazyRoute = lazyWithFallback(() => import('pages/main-menu'));
const MenuContent: LazyRoute = lazyWithFallback(() => import('pages/menu-content'));
const Home: LazyRoute = lazyWithFallback(() => import('pages/home'));
const Account: LazyRoute = lazyWithFallback(() => import('pages/account'));
const CommPreferences: LazyRoute = lazyWithFallback(() => import('pages/account/comm-preferences'));
const AccountInfo: LazyRoute = lazyWithFallback(() => import('pages/account/account-info'));
const AccountDelete: LazyRoute = lazyWithFallback(() => import('pages/account/account-delete'));
const AccountRequestInfo: LazyRoute = lazyWithFallback(
  () => import('pages/account/account-request-info')
);
const PaymentMethods: LazyRoute = lazyWithFallback(() => import('pages/account/payment-methods'));
const PaybackVendor: LazyRoute = lazyWithFallback(
  () => import('pages/account/payback-vendor/payback-vendor')
);
const PaybackLegalTexts: LazyRoute = lazyWithFallback(
  () => import('pages/account/payback-legal-texts')
);
const Diagnostics: LazyRoute = lazyWithFallback(() => import('pages/diagnostics'));
const AccountOrders: LazyRoute = lazyWithFallback(() => import('pages/account/account-orders'));
const Offers: LazyRoute = lazyWithFallback(() => import('pages/offers'));
const LoyaltyOffers: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-offers'));
const LoyaltyRewards: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-rewards'));
const LoyaltyDashboard: LazyRoute = lazyWithFallback(
  () => import('pages/loyalty/loyalty-dashboard')
);
const LoyaltySurprise: LazyRoute = lazyWithFallback(() => import('pages/loyalty/loyalty-surprise'));
const LoyaltyInRestaurantRedemption: LazyRoute = lazyWithFallback(
  () => import('pages/loyalty/loyalty-in-restaurant-redemption')
);
const LoyaltyClaimPoints: LazyRoute = lazyWithFallback(
  () => import('pages/loyalty/loyalty-claim-points')
);
const LoyaltyLinkCard: LazyRoute = lazyWithFallback(
  () => import('pages/loyalty/loyalty-link-card')
);
const SignupHome: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-up-home'));
const AuthChallengeScreenJwt: LazyRoute = lazyWithFallback(
  () => import('pages/authentication/auth-email-jwt')
);
const ConfirmJwt: LazyRoute = lazyWithFallback(() => import('pages/authentication/confirm-jwt'));
const SignOut: LazyRoute = lazyWithFallback(() => import('pages/authentication/sign-out'));
const StoreLocator: LazyRoute = lazyWithFallback(() => import('pages/store-locator'));
const OrderConfirmation: LazyRoute = lazyWithFallback(
  () => import('pages/order-confirmation/order-confirmation')
);
const CateringBirthday: LazyRoute = lazyWithFallback(() => import('pages/catering-birthday'));

const StoreOrderConfirmation: LazyRoute = lazyWithFallback(
  () => import('pages/order-confirmation/store/store-order-confirmation')
);
const About: LazyRoute = lazyWithFallback(() => import('pages/about'));
const StaticPage: LazyRoute = lazyWithFallback(() => import('pages/static'));
const NotFound: LazyRoute = lazyWithFallback(() => import('pages/not-found'));
const SupportPage: LazyRoute = lazyWithFallback(() => import('pages/support'));
const SupportV2CategoriesPage: LazyRoute = lazyWithFallback(
  () => import('pages/support-v2/support-v2-categories-page')
);
const SupportV2CategoryPage: LazyRoute = lazyWithFallback(
  () => import('pages/support-v2/support-v2-category-page')
);
const SupportV2FormPage: LazyRoute = lazyWithFallback(
  () => import('pages/support-v2/support-v2-form-page')
);
const SupportV2OrdersListPage: LazyRoute = lazyWithFallback(
  () => import('pages/support-v2/support-v2-orders-list-page')
);
const ReportOrderIssuePage: LazyRoute = lazyWithFallback(() => import('pages/report-order-issue'));
const ReportOrderNeverArrivedPage: LazyRoute = lazyWithFallback(
  () => import('pages/report-order-never-arrived')
);
const ReportOrderItemsMissingPage: LazyRoute = lazyWithFallback(
  () => import('pages/report-order-items-missing')
);
const OfferActivatePage: LazyRoute = lazyWithFallback(() => import('pages/offer-activate'));
const History = lazyWithFallback(() => import('pages/account/history'));
const FavoriteEditorPage = lazyWithFallback(() => import('pages/favorite-editor'));
const LoadingPage = lazyWithFallback(() => import('pages/loading'));

const AccountSettings: LazyRoute = lazyWithFallback(() => import('pages/account/account-settings'));
const SettingsLocation: LazyRoute = lazyWithFallback(
  () => import('pages/account/account-settings/settings-location')
);
const InvitationCode: LazyRoute = lazyWithFallback(() => import('pages/account/invitation-code'));

const Memberships: LazyRoute = lazyWithFallback(() => import('pages/account/memberships'));
const MembershipDetailsPage: LazyRoute = lazyWithFallback(
  () => import('pages/account/membership-details')
);
const BlueLightCard: LazyRoute = lazyWithFallback(() => import('pages/account/blue-light-card'));

const BkpayRedirect: LazyRoute = lazyWithFallback(() => import('pages/bkpay/index'));

const AppContainer = styled(View)`
  height: 100%;
  width: 100%;
  flex: 1;
`;

const LayoutContainerScrollView = styled(ScrollView)`
  background: ${Styles.color.background};
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow-y: scroll;
  overflow-x: hidden;
  &:focus {
    inset-block-start: 0.2rem;
    inset-inline-start: 0.2rem;
    opacity: 1;
  }
`;

const ContentContainer = styled(View)`
  background: ${Styles.color.background};
  flex-direction: column;
  flex: 1;
`;

const ContentLoadingContainer = styled(View)`
  width: 100%;
  flex: 1;
  align-items: center;
  justify-content: center;
  background: ${Styles.color.background};
`;

const RouterWrapperContainer = styled(View)<{ $windowHeight: number }>`
  min-height: ${(p: { $windowHeight: number }) => p.$windowHeight}px;
  background: ${Styles.color.background};
  flex-grow: 1;
`;

const RouterWrapper: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <RouterWrapperContainer $windowHeight={window.innerHeight}>{children}</RouterWrapperContainer>
  );
};

const ContentRouting = () => {
  const { clearServerOrder } = useOrderContext(); // @todo type order context

  const { formatMessage } = useIntl();
  const loyaltyEnabled = useIsLoyaltyEnabled();
  const { claimPointsEnabled, loyaltySurpriseOffersEnabled } = useLoyaltyContext();

  const enableCatering = useFlag(LaunchDarklyFlag.ENABLE_CATERING);
  const enableOrderSupport = useFlag(LaunchDarklyFlag.ENABLE_ORDER_SUPPORT);
  const enableAccountDeletePage = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_DELETE_PAGE);

  const enableRequestMyInformation = useFlag(LaunchDarklyFlag.ENABLE_REQUEST_MY_INFORMATION);
  const enableCommPrefFlag = useFlag(LaunchDarklyFlag.ENABLE_COMMUNICATION_PREFERENCES);
  const enableCommunicationPreferences = !isFalse(enableCommPrefFlag);

  const enableGuestAndMultiStepSupportForm = useFlag(
    LaunchDarklyFlag.ENABLE_GUEST_AND_MULTI_STEP_SUPPORT_PAGE
  );

  const { isBluecodeEnabled } = useBluecodeContext();

  const enablePayback = useFlag(LaunchDarklyFlag.ENABLE_PAYBACK);

  const enableSignUpFlag = useFlag(LaunchDarklyFlag.ENABLE_SIGN_UP);
  const enableSignUp = isNil(enableSignUpFlag) || enableSignUpFlag;

  const forceAuthOnHomepageFlag = useFlag(LaunchDarklyFlag.FORCE_HOMEPAGE_AUTH);
  const isBaseRoute = useMatch(routes.base);

  const enableRewardPageStandAlone = useFlag(LaunchDarklyFlag.ENABLE_REWARD_PAGE_STAND_ALONE);

  const forceAuthOnHomepage = forceAuthOnHomepageFlag && enableSignUp && isBaseRoute;

  const enableRewardsStaticPage = useFlag(LaunchDarklyFlag.ENABLE_REWARDS_STATIC_PAGE);
  const enableOffersStaticPage = useFlag(LaunchDarklyFlag.ENABLE_OFFERS_STATIC_PAGE);

  const enableAccountSettings = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_SETTINGS);
  const enableGeolocation = useFlag(LaunchDarklyFlag.ENABLE_SETTINGS_GEOLOCATION);

  const enableCateringBirthday = useFlag(LaunchDarklyFlag.ENABLE_CATERING_BIRTHDAY);

  const { enableInvitationCode } = useFeatureInvitationCode();

  const { enableMemberships } = useFeatureMemberships();

  useAuthRedirects({ skip: !forceAuthOnHomepage });

  useEffect(() => {
    // reset the server order when a user navigate into the menu no matter what
    clearServerOrder();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Base Routes */}
      <ContentContainer>
        <RouterWrapper>
          <Routes>
            <Route path={'*'} element={<StaticPage />} />
            <Route path={routes.notFound404} element={<NotFound />} />
            <Route path={routes.confirmJwt} element={<ConfirmJwt />} />
            {loyaltyEnabled && (
              <Route
                path={`${formatMessage({ id: 'routes.loyaltyDashboard' })}`}
                element={<LoyaltyDashboard />}
              />
            )}
            {loyaltySurpriseOffersEnabled && (
              <Route
                path={`${formatMessage({ id: 'routes.loyaltyHome' })}/${formatMessage({
                  id: 'surprise',
                })}`}
                element={<LoyaltySurprise />}
              />
            )}
            <Route
              path={formatMessage({ id: 'routes.redemptionInRestaurant' })}
              element={<LoyaltyInRestaurantRedemption />}
            />
            <Route
              path={formatMessage({ id: 'routes.redeem-is-not-available-at-this-restaurant' })}
              element={<LoyaltyNotAvailableAtThisRestaurant />}
            />
            <Route
              path={formatMessage({ id: 'routes.redeem-is-not-available-at-delivery-mode' })}
              element={<LoyaltyNotAvailableAtDeliveryMode />}
            />
            <Route
              path={formatMessage({ id: 'routes.linkPhysicalCard' })}
              element={<LoyaltyLinkCard />}
            />
            {claimPointsEnabled && (
              <Route
                path={`${formatMessage({ id: 'routes.claimPointsBase' })}/*`}
                element={<LoyaltyClaimPoints />}
              />
            )}
            {enableSignUp && (
              <>
                <Route path={routes.signUp} element={<SignupHome />} />
                <Route path={routes.signIn} element={<SignupHome />} />
                <Route path={routes.confirmOtp} element={<SignupHome />} />
                {/* TODO(ICFE-804): verify if the localized account route is
                    being linked from anywhere. */}
                <Route path={formatMessage({ id: 'routes.account' })} element={<Account />} />
                <Route path={routes.account} element={<Account />} />
              </>
            )}
            <Route path={routes.authChallengeJwt} element={<AuthChallengeScreenJwt />} />
            <Route path={routes.signOut} element={<SignOut />} />
            <Route path={routes.menu} element={<MainMenu />} />
            <Route path={routes.browseMenu} element={<MainMenu />} />

            {/*
                MenuContent handles the following routes:

                /menu/favorites
                /menu/recent-items
                /menu/section-:id
                /menu/picker-:id
                /menu/combo-:id
                /menu/item-:id
                /menu/* (Item Not Found)

              */}
            <Route path={`${routes.menu}/:id`} element={<MenuContent />} />
            <Route path={`${routes.browseMenu}/:id`} element={<MenuContent />} />

            <Route path={routes.base} element={<Home />} />
            {!enableOffersStaticPage && <Route path={`${routes.offers}/*`} element={<Offers />} />}
            {enableRewardPageStandAlone && (
              <Route
                path={formatMessage({ id: 'routes.loyaltyHome' })}
                element={<LoyaltyDashboard />}
              />
            )}

            {!enableRewardsStaticPage && (
              <>
                <Route
                  path={formatMessage({ id: 'routes.loyaltyOfferList' })}
                  element={<LoyaltyOffers />}
                />
                <Route
                  path={`${formatMessage({ id: 'routes.loyaltyOfferList' })}/*`}
                  element={<LoyaltyOffers />}
                />
                <Route
                  path={formatMessage({ id: 'routes.loyaltyRewardList' })}
                  element={<LoyaltyRewards />}
                />
                <Route
                  path={`${formatMessage({ id: 'routes.loyaltyRewardList' })}/*`}
                  element={<LoyaltyRewards />}
                />
              </>
            )}

            <Route path={routes.info} element={<AccountInfo />} />
            <Route
              path={`${formatMessage({ id: 'routes.rewardsHistory' })}`}
              element={<History />}
            />
            {enableCommunicationPreferences && (
              <Route path={routes.communication} element={<CommPreferences />} />
            )}
            <Route path={`${routes.orders}/*`} element={<AccountOrders />} />
            {enableAccountDeletePage && (
              <Route
                path={formatMessage({ id: 'routes.accountDelete' })}
                element={<AccountDelete />}
              />
            )}
            {enableRequestMyInformation && (
              <Route
                path={formatMessage({ id: 'routes.accountRequestInfo' })}
                element={<AccountRequestInfo />}
              />
            )}
            <Route path={`${routes.payment}/*`} element={<PaymentMethods />} />
            {enablePayback && <Route path={`${routes.payback}`} element={<PaybackVendor />} />}
            {enablePayback && (
              <Route path={`${routes.paybackLegalTexts}`} element={<PaybackLegalTexts />} />
            )}
            <Route path={routes.about} element={<About />} />
            <Route path={routes.aboutApp} element={<AboutApp />} />
            <Route path={`${routes.diagnostics}/*`} element={<Diagnostics />} />
            {enableOrderSupport && (
              <Route
                path={`${routes.reportOrderIssue}/:orderId`}
                element={<ReportOrderIssuePage />}
              />
            )}
            {enableOrderSupport && (
              <Route
                path={`${routes.missingOrder}/:orderId`}
                element={<ReportOrderNeverArrivedPage />}
              />
            )}
            {enableOrderSupport && (
              <Route
                path={`${routes.missingItems}/:orderId`}
                element={<ReportOrderItemsMissingPage />}
              />
            )}
            {enableInvitationCode && (
              <Route path={`${routes.invitationCode}`} element={<InvitationCode />} />
            )}
            <Route
              path={`${routes.offerActivate}/:tokenId/:offerId`}
              element={<OfferActivatePage />}
            />
            {enableGuestAndMultiStepSupportForm ? (
              <>
                {/* // general support requests */}
                <Route path={routes.supportForm} element={<SupportV2FormPage />} />
                <Route path={routes.supportOrders} element={<SupportV2OrdersListPage />} />
                <Route path={routes.supportCategories} element={<SupportV2CategoriesPage />} />
                <Route path={routes.supportCategory} element={<SupportV2CategoryPage />} />
                <Route path={routes.support} element={<SupportV2CategoriesPage />} />
              </>
            ) : (
              <Route path={routes.support} element={<SupportPage />} />
            )}

            <Route path={routes.favoriteEditor} element={<FavoriteEditorPage />} />

            {/* We only want brand-specific routes to be available for those brands */}
            {/* `brandRoutes` can be thought of a Map of routes (string) to Components (React component) */}
            {(Object.entries(brandRoutes) as Array<[keyof typeof brandRoutes, LazyRoute]>).map(
              ([path, Component]) =>
                // if a route isn't catering we want it. if it is catering, we leave it up to the LD flag
                path !== routes.catering || enableCatering ? (
                  <Route
                    path={formatMessage({ id: path })}
                    element={<Component key={path} />}
                    key={path}
                  />
                ) : null
            )}

            {enableAccountSettings && (
              <Route path={routes.settings} element={<AccountSettings />} />
            )}

            {enableMemberships && (
              <>
                <Route
                  path={formatMessage({ id: 'routes.memberships' })}
                  element={<Memberships />}
                />
                <Route
                  path={formatMessage({ id: 'routes.membershipDetails' })}
                  element={<MembershipDetailsPage />}
                />
                <Route
                  path={formatMessage({ id: 'routes.blueLightCard' })}
                  element={<BlueLightCard />}
                />
              </>
            )}

            {enableGeolocation && (
              <Route path={routes.settingsLocation} element={<SettingsLocation />} />
            )}
            {enableCateringBirthday && <Route path={routes.cateringBirthday} />}
            {isBluecodeEnabled && (
              <>
                <Route path={routes.bkPay} element={<BkpayRedirect />} />
                <Route path={routes.bkPayPaymentRequest} element={<Home />} />
              </>
            )}
          </Routes>
        </RouterWrapper>
        <Footer />
      </ContentContainer>
    </>
  );
};

const Layout = () => {
  const {
    location: { pathname },
    navigate,
  } = useLocationContext();
  const {
    featureAccountId = '',
    featureIdsLoading,
    featureAccountFormId = '',
    featureMenuId = '',
  } = useFeaturesContext();
  const { routesLoading } = usePageManagerContext();
  const { scrollRef, scrollTo } = useScrollContext();
  const [IOSTrackingStatus, setIOSTrackingStatus] = useState<null | string>(null);
  const { loading: isFeatureAccountLoading } = useFeatureAccountQuery({
    variables: { featureAccountId },
    skip: !featureAccountId || pathname !== routes.communication || pathname !== routes.orders,
  });
  const { featureNavigationLoading: isFeatureNavigationLoading } = useFeatureNavigation();
  const { loading: isTrendingContentLoading } = useTrendingData();
  const { loading: isMainMenuContentLoading } = useFeatureMenuQuery({
    variables: { featureMenuId },
    skip: !featureMenuId || pathname !== routes.menu,
  });

  const { loading: isSignUpContentLoading } = useFeatureAccountFormQuery({
    variables: { featureAccountFormId },
    skip: !featureAccountFormId || pathname !== routes.signUp || pathname !== routes.signIn,
    fetchPolicy: 'cache-and-network',
  });

  const topServiceModeFlag = useFlag(LaunchDarklyFlag.ENABLE_TOP_SERVICE_MODE);
  const bottomServiceModeFlag = useFlag(LaunchDarklyFlag.ENABLE_BOTTOM_SERVICE_MODE);
  const enableCateringBirthday = useFlag(LaunchDarklyFlag.ENABLE_CATERING_BIRTHDAY);
  const enableTopServiceMode = topServiceModeFlag && !bottomServiceModeFlag;
  const enableBottomServiceMode = bottomServiceModeFlag && !topServiceModeFlag;
  const permissionToUpdate = askPermissionOnCritialRoute() && getShouldReloadApp();

  useEffect(() => {
    scrollTo({ x: 0, y: 0, animated: false });
  }, [scrollTo, pathname]);

  async function trackingStatus() {
    const iosstatus = await getIosAppTrackingStatus();
    setIOSTrackingStatus(iosstatus);
  }

  useEffectOnce(() => {
    // Prompt users to enable push notifications, the prompt is displayed only once
    // This should call mParticle.trackEvent but for some reason it dispatch multiple Update User Attribute events
    enablePush({});
    trackingStatus();
  });

  /* TODO: figure out how to get the loading status
   *       without double-triggering the Sanity call
   * CONTEXT:
   *  this code still fires off the Sanity query even
   *    though it is already fired from useFeatureHomePage hook
   *  setting skip: true means that loading always comes back false
   *  not passing anything means we still hit Sanity, but it returns
   *    an error that id is required
   *  💭 maybe moving feature home page data to context so that we
   *    have the data and loading state available multiple places?
   */
  const { featureHomePageLoading: isFeatureHomePageLoading } = useFeatureHomePage();
  const { loading: isStaticRoutesLoading } = useStaticPageRoutes();

  const { featureInvitationCodeLoading } = useFeatureInvitationCode();

  useForter();
  useLogPageView();
  useScrollTop();
  useGeoData();
  const { language } = useLocale();
  const { formatMessage } = useIntl();

  // Prompts user for location services at start of session and selects nearest store
  const enableAutoSelectRestaurant = useFlag(LaunchDarklyFlag.ENABLE_AUTO_SELECT_RESTAURANT);
  const geoLocationAvailable = navigator.geolocation;

  const [canonicalUrl] = window.location.href.split('?');
  const canonicalLink = [{ rel: 'canonical', href: canonicalUrl }];

  const { requestIosAttPermissions } = useIosAtt();

  let content;

  useEffectOnce(() => {
    const nextLocationQueryStringParam = new URL(window.location.href).searchParams.get(
      NEXT_URL_QUERY_PARAM
    );

    if (nextLocationQueryStringParam) {
      const decodedNexRoute = decodeURIComponent(nextLocationQueryStringParam);

      // Check for relative path but do not accept schemaless urls to avoid open redirect vulnerability
      if (/^\/[^/]/.test(decodedNexRoute)) {
        // React router will not let us call `navigate` on initial render
        setTimeout(() => {
          navigate(decodedNexRoute, { replace: true });
        }, 1);
      }
    }
  });

  useResetStoreResetCartResetServiceModeTimeout();

  useEffect(requestIosAttPermissions, []);

  // Temporary solution, the goal is to make object for each pathname
  const getLoadingAriaLabel = (path: string) => {
    if (path === routes.menu) {
      return formatMessage({ id: 'loadingRestaurantMenu' });
    }
    return formatMessage({ id: 'loading' });
  };

  const blockingLoadingStates = (path: string) => {
    // The ultimate goal here is to preload what the critical pages will use,
    // without holding up the user waiting for content that isn't needed on the page.
    // As we extend this pattern, more routes should be added to this switch statement.
    // This should become the only "full page spinner" in the app, so we know it when we see it.
    switch (path) {
      case routes.base:
        return [featureIdsLoading, isFeatureHomePageLoading];
      case routes.offers:
        return [featureIdsLoading];
      case routes.about:
        return [featureIdsLoading, isFeatureNavigationLoading];
      case routes.orders:
      case routes.communication:
        return [featureIdsLoading, isFeatureAccountLoading];
      case routes.menu:
        return [featureIdsLoading, isMainMenuContentLoading];
      case routes.signUp:
        return [featureIdsLoading, isSignUpContentLoading];
      case routes.trending:
        return [featureIdsLoading, isTrendingContentLoading];
      case routes.storeOrderConfirmation:
        return [featureIdsLoading, routesLoading];
      case routes.invitationCode:
        return [featureIdsLoading, featureInvitationCodeLoading];
      case routes.cateringBirthday:
      default:
        return [routesLoading, isStaticRoutesLoading];
    }
  };

  if (blockingLoadingStates(pathname).some(queryIsLoading => !!queryIsLoading)) {
    content = (
      <ContentLoadingContainer aria-label={getLoadingAriaLabel(pathname)}>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </ContentLoadingContainer>
    );
  } else {
    content = (
      <LayoutContainerScrollView data-testid="main-layout" ref={scrollRef}>
        <main id="main">
          <Suspense fallback={<SetLoading />}>
            <Routes>
              {/* Full page routes */}
              {enableCateringBirthday && (
                <Route path={routes.cateringBirthday} element={<CateringBirthday />} />
              )}
              <Route
                path={`${routes.cart}/*`}
                element={<Cart permissionToUpdate={permissionToUpdate} />}
              />
              <Route path={routes.storeOrderConfirmation} element={<StoreOrderConfirmation />} />
              <Route
                path={`${routes.orderConfirmation}/:rbiOrderId`}
                element={<OrderConfirmation />}
              />
              <Route path={`${routes.storeLocator}/*`} element={<StoreLocator />} />
              <Route path={`${routes.languageSelector}`} element={<LanguageSelectorPage />} />
              <Route
                path={`${formatMessage({ id: 'routes.storeLocator' })}/*`}
                element={<StoreLocator />}
              />
              <Route path={routes.loading} element={<LoadingPage />} />
              {(
                Object.entries(fullPageBrandRoutes) as Array<
                  [keyof typeof fullPageBrandRoutes, LazyRoute]
                >
              ).map(([path, Component]) => (
                <Route
                  path={formatMessage({ id: path })}
                  element={<Component key={path} />}
                  key={path}
                />
              ))}
              {/* Regular routes (with normal nav, header, footer, etc) */}
              <Route path="*" element={<ContentRouting />} />
            </Routes>
          </Suspense>
        </main>
      </LayoutContainerScrollView>
    );
  }

  return (
    <>
      <Helmet link={canonicalLink}>
        <html lang={language} dir={getWritingDir(language)} />
      </Helmet>
      {shouldUseRobotsMetaTag(pathname) && <Meta name="robots" content="none" />}

      <AppContainer>
        <AppHeader />
        {content}
        <LoyaltyOptInModal />
        {enableAutoSelectRestaurant && geoLocationAvailable && <AutoSelectRestaurant />}
        <ModalBeeper />
        <ModalLockerPickup />

        <View data-testid="footer">
          <FabContainer />
          <ToastContainer />
          <CookieBanner hasUserDeniedTracking={IOSTrackingStatus === Status.DENIED} />
          {pathname === routes.base && <BottomSheetRefillDrinks />}
          {enableTopServiceMode && <TopServiceModeNotification />}

          <LayoutBottomContainer>
            {enableBottomServiceMode && <BottomServiceMode />}
            <MobileNavigation />
          </LayoutBottomContainer>
        </View>
        <ForceUpdateModal />
      </AppContainer>
      <ErrorNetwork />
    </>
  );
};

export default Layout;
